import React, { useContext } from 'react';
import { gql } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { PageNavMenuItem } from '@components-clean/page-nav/page-nav-menu-item';
import { ActiveItemPerLevelInfo } from '@components-clean/page-nav/types';
import doLogout from 'utils/auth.service';
import { UserContext } from '@contexts/user-context';
import Constants from '@utils/constants';
import { Logger } from '@utils/logger';
import NotificationService from '@utils/notification';

interface ReseedDataProps {
    activeItemsInfo: ActiveItemPerLevelInfo;
    isPageNavCollapsed?: boolean;
    searchActive: boolean;
    searchTerm: string;
}

export const RESEED_ORG_DATA = gql`
    mutation reseedCurrentOrgForTesting($seed: ConvertableInt) {
        reseedCurrentOrgForTesting(seed: $seed)
    }
`;

const ReseedData = ({ activeItemsInfo, isPageNavCollapsed, searchActive = false, searchTerm }: ReseedDataProps) => {
    const user = useContext(UserContext);
    const client = useApolloClient();
    const [reseedData, { loading: seeding }] = useMutation<{ reseedCurrentOrgForTesting: unknown }>(RESEED_ORG_DATA);

    const showSeedMenuItem =
        !seeding &&
        (user?.user?.systemAdminLevel ?? 0) >= Constants.SystemAdminLevel.Administrator &&
        window.environment !== Constants.environment.PROD;

    const onClick = () => {
        // eslint-disable-next-line no-alert
        const confirmSelected = window.confirm(
            'This will clear and replace all studies within your current organisation with random seed data.  Are you sure you want to do this?',
        );
        if (!confirmSelected) return;

        reseedData({ variables: { seed: 0 } })
            .then((result) => {
                Logger.log('Reseed result: ', result);

                // logout after reseed is done
                if (result.data?.reseedCurrentOrgForTesting === null) {
                    doLogout(client, user, 'ReseedCurrentOrgForTesting');
                } else if ('error' in result) {
                    NotificationService.showNotification(JSON.stringify(result.error));
                } else if (result.errors?.at(0)) {
                    NotificationService.showNotification(JSON.stringify(result.errors.at(0)));
                }
            })
            .catch(() => {
                NotificationService.showNotification('Failed to Fetch. Please try again.');
            });
    };

    return (
        <>
            {!isPageNavCollapsed && seeding && <span>Seeding...</span>}
            {showSeedMenuItem && (
                <PageNavMenuItem
                    isPageNavCollapsed={isPageNavCollapsed}
                    faIcon={['far', 'backward-fast']}
                    activeItemsInfo={activeItemsInfo}
                    id="reseed"
                    label={isPageNavCollapsed ? '' : 'Reseed Data'}
                    itemKey="nav-reseed"
                    key="sidebar-main-li-reseed"
                    onClick={onClick}
                    searchActive={searchActive}
                    searchTerm={searchTerm}
                />
            )}
        </>
    );
};

export default ReseedData;
